/**
 * Cart drawer (sections/cart-drawer.liquid)
 *
 * Cart updates go through the Ajax Cart API with the Section Rendering API
 * (`sections: 'cart-drawer'`), so the drawer HTML (items, totals, free shipping
 * bar) always comes from Liquid. Recommendations come from the Search & Discovery
 * product recommendations endpoint, rendered by
 * sections/cart-drawer-recommendations.liquid.
 *
 * Public API: window.RWCartDrawer.open() / .close() / .refresh()
 */
(function() {
  var SECTION_ID = 'cart-drawer';
  var RECS_SECTION_ID = 'cart-drawer-recommendations';
  var MAX_RECS_SOURCES = 3;

  var drawer = document.querySelector('[data-rw-cart-drawer]');
  if (!drawer) return;

  var panel = drawer.querySelector('#fly-out-cart-content-wrapper');
  var recsEnabled = drawer.dataset.recsEnabled === 'true';
  var recsLimit = parseInt(drawer.dataset.recsLimit, 10) || 2;
  var recsIntents = drawer.dataset.recsIntent === 'related' ? ['related'] : ['complementary', 'related'];
  var recsUrl = drawer.dataset.recsUrl || '/recommendations/products';
  var recsCache = {};
  var recsKey = null;
  var lastRenderedAt = 0;
  var previouslyFocused = null;
  var qtyTimers = {};
  var closeTimer = null;
  var CLOSE_DURATION = 350; // keep in sync with the close animation in sections/cart-drawer.liquid

  /* ---------- open / close ---------- */

  function isOpen() {
    return drawer.classList.contains('open') && !drawer.classList.contains('is-closing');
  }

  function open() {
    if (drawer.classList.contains('is-closing')) {
      // Reopened mid-close: cancel the close animation and stay open.
      clearTimeout(closeTimer);
      drawer.classList.remove('is-closing');
      return;
    }
    if (isOpen()) return;
    previouslyFocused = document.activeElement;
    drawer.classList.add('open');
    panel.classList.add('open');
    document.documentElement.classList.add('rw-cart-drawer-open');
    panel.focus({ preventScroll: true });
  }

  // Plays the close animation, then hides the drawer (the legacy CSS hides it
  // as soon as the `open` class is removed).
  function close() {
    if (!isOpen()) return;
    drawer.classList.add('is-closing');
    document.documentElement.classList.remove('rw-cart-drawer-open');
    if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus({ preventScroll: true });
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    closeTimer = setTimeout(function() {
      drawer.classList.remove('is-closing');
      drawer.classList.remove('open');
      panel.classList.remove('open');
    }, reduceMotion ? 0 : CLOSE_DURATION);
  }

  document.addEventListener('click', function(e) {
    if (e.target.closest('#nav-link-icon')) {
      e.preventDefault();
      open();
      return;
    }
    if (e.target.closest('[data-rw-cart-close]') && drawer.contains(e.target)) {
      e.preventDefault();
      close();
    }
  });

  document.addEventListener('keydown', function(e) {
    if ((e.key === 'Enter' || e.key === ' ') && e.target.id === 'nav-link-icon') {
      e.preventDefault();
      open();
      return;
    }
    if (e.key === 'Escape' && isOpen()) close();
  });

  // Legacy scripts (e.g. templates/page.subscribe.liquid) open the drawer by adding
  // the `open` class after their own /cart/add.js call; refresh so it shows the new item.
  new MutationObserver(function() {
    if (isOpen()) {
      document.documentElement.classList.add('rw-cart-drawer-open');
      if (Date.now() - lastRenderedAt > 1500) refresh();
    } else {
      document.documentElement.classList.remove('rw-cart-drawer-open');
    }
  }).observe(drawer, { attributes: true, attributeFilter: ['class'] });

  /* ---------- rendering ---------- */

  function parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function renderDrawer(html) {
    if (!html) return;
    var doc = parse(html);
    var newBody = doc.querySelector('[data-rw-cart-body]');
    var body = drawer.querySelector('[data-rw-cart-body]');
    if (newBody && body) {
      // Keep the recommendations already loaded; loadRecommendations() refreshes them if the cart's products changed.
      var oldRecs = body.querySelector('[data-rw-recs]');
      body.innerHTML = newBody.innerHTML;
      var newRecs = body.querySelector('[data-rw-recs]');
      if (oldRecs && newRecs) newRecs.replaceWith(oldRecs);

      // Header bubble and nav icon (sections/jc-header-update.liquid)
      var count = newBody.dataset.rwItemCount || '0';
      body.dataset.rwItemCount = count;
      document.querySelectorAll('[data-rw-cart-count]').forEach(function(el) {
        el.textContent = count;
        if (el.classList.contains('rw-nav-cart__count')) el.hidden = count === '0';
      });
    }
    lastRenderedAt = Date.now();
    document.dispatchEvent(new CustomEvent('rw:cart:updated'));
    loadRecommendations();
  }

  function setLoading(isLoading) {
    drawer.classList.toggle('is-loading', isLoading);
  }

  function refresh() {
    setLoading(true);
    return fetch(window.location.pathname + '?sections=' + SECTION_ID, { credentials: 'same-origin' })
      .then(function(res) { return res.json(); })
      .then(function(sections) { renderDrawer(sections[SECTION_ID]); })
      .catch(function(err) { console.error('[cart-drawer] refresh failed', err); })
      .finally(function() { setLoading(false); });
  }

  /* ---------- add to cart ---------- */

  function showFormError(form, message) {
    var el = form.querySelector('[data-rw-rec-error], .rw-cart-error');
    if (!el) {
      el = document.createElement('div');
      el.className = 'rw-cart-error rw-rec-card__error';
      form.appendChild(el);
    }
    el.textContent = message;
    el.hidden = !message;
  }

  document.addEventListener('submit', function(e) {
    var form = e.target;
    if (e.defaultPrevented || !form.matches || !form.matches('form[action$="/cart/add"]')) return;
    e.preventDefault();

    var submitter = e.submitter || form.querySelector('[type="submit"]');
    var formData = new FormData(form);
    formData.append('sections', SECTION_ID);
    formData.append('sections_url', window.location.pathname);

    showFormError(form, '');
    if (submitter) submitter.setAttribute('aria-busy', 'true');
    if (submitter) submitter.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
      credentials: 'same-origin'
    })
      .then(function(res) {
        return res.json().then(function(json) { return { ok: res.ok, json: json }; });
      })
      .then(function(result) {
        if (!result.ok) {
          showFormError(form, result.json.description || result.json.message || 'Could not add to cart.');
          return;
        }
        renderDrawer(result.json.sections && result.json.sections[SECTION_ID]);
        // Adds from inside the drawer (recommendations) skip the animation.
        if (drawer.contains(form)) return open();
        var item = result.json.items ? result.json.items[0] : result.json;
        flyToCart(item && (item.image || (item.featured_image && item.featured_image.url)), submitter || form)
          .then(open);
      })
      .catch(function(err) {
        console.error('[cart-drawer] add failed', err);
        showFormError(form, 'Could not add to cart.');
      })
      .finally(function() {
        if (submitter) {
          submitter.disabled = false;
          submitter.removeAttribute('aria-busy');
        }
      });
  });

  /* ---------- fly to cart (ported from the Melo theme's assets/product-form.js) ---------- */

  function bezierPoint(t, p0, p1, p2, p3) {
    var u = 1 - t;
    return {
      x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x,
      y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y
    };
  }

  function bumpCartIcon() {
    document.querySelectorAll('.rw-nav-cart__count').forEach(function(el) {
      el.classList.remove('is-bumping');
      void el.offsetWidth; // restart the animation
      el.classList.add('is-bumping');
    });
  }

  // Flies a thumbnail of the added product from the button to the nav cart icon.
  // Resolves when it lands (immediately if there's nothing to animate).
  function flyToCart(imageUrl, source) {
    var cartIcon = document.getElementById('nav-link-icon');
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!imageUrl || !source || !cartIcon || reduceMotion) return Promise.resolve();

    var destinationRect = cartIcon.getBoundingClientRect();
    if (!destinationRect.width || destinationRect.bottom < 0) return Promise.resolve();

    var flyer = document.createElement('div');
    flyer.className = 'rw-fly-to-cart';
    flyer.style.backgroundImage = 'url("' + imageUrl + (imageUrl.indexOf('?') > -1 ? '&' : '?') + 'width=120")';
    document.body.appendChild(flyer);

    var rect = flyer.getBoundingClientRect();
    var sourceRect = source.getBoundingClientRect();
    var offset = { x: rect.width / 2, y: rect.height / 2 };
    var startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y
    };
    var endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y
    };
    var controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // up 200px
    var controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // left 300px, up 100px
    var duration = 600;

    flyer.style.setProperty('--x', startPoint.x + 'px');
    flyer.style.setProperty('--y', startPoint.y + 'px');
    flyer.style.opacity = '1';

    return new Promise(function(resolve) {
      var startTime = null;
      function step(now) {
        if (!startTime) startTime = now;
        var progress = Math.min((now - startTime) / duration, 1);
        var position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);
        flyer.style.setProperty('--x', position.x + 'px');
        flyer.style.setProperty('--y', position.y + 'px');
        flyer.style.setProperty('--scale', String(1 - progress * 0.5));

        if (progress < 1) return requestAnimationFrame(step);
        flyer.style.opacity = '0';
        setTimeout(function() { flyer.remove(); }, 300);
        bumpCartIcon();
        resolve();
      }
      requestAnimationFrame(step);
    });
  }

  /* ---------- discount codes (ported from the Melo theme's assets/cart-discount.js) ---------- */

  function appliedDiscountCodes(root) {
    return Array.prototype.map.call(root.querySelectorAll('[data-rw-discount-code]'), function(el) {
      return el.dataset.rwDiscountCode;
    });
  }

  function showDiscountError(type) {
    var wrapper = drawer.querySelector('[data-rw-discount-error]');
    if (!wrapper) return;
    wrapper.hidden = !type;
    wrapper.querySelectorAll('[data-rw-discount-error-type]').forEach(function(el) {
      el.hidden = el.dataset.rwDiscountErrorType !== type;
    });
  }

  function updateDiscounts(codes) {
    setLoading(true);
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        discount: codes.join(','),
        sections: [SECTION_ID],
        sections_url: window.location.pathname
      }),
      credentials: 'same-origin'
    })
      .then(function(res) { return res.json(); })
      .finally(function() { setLoading(false); });
  }

  function lower(code) {
    return code.toLowerCase();
  }

  drawer.addEventListener('submit', function(e) {
    var form = e.target.closest('[data-rw-discount-form]');
    if (!form) return;
    e.preventDefault();

    var input = form.querySelector('[name="discount"]');
    var code = input.value.trim();
    var existing = appliedDiscountCodes(drawer);
    if (!code || existing.map(lower).indexOf(lower(code)) > -1) return;

    showDiscountError(null);
    updateDiscounts(existing.concat(code))
      .then(function(json) {
        var result = (json.discount_codes || []).filter(function(d) {
          return lower(d.code) === lower(code);
        })[0];
        if (result && !result.applicable) {
          input.value = '';
          return showDiscountError('code');
        }

        var html = json.sections && json.sections[SECTION_ID];
        // A valid code that doesn't show up as applied is a shipping discount,
        // which Shopify only applies once there's a shipping address.
        if (html && result && appliedDiscountCodes(parse(html)).map(lower).indexOf(lower(code)) === -1) {
          input.value = '';
          return showDiscountError('shipping');
        }
        renderDrawer(html);
      })
      .catch(function(err) {
        console.error('[cart-drawer] discount failed', err);
        showDiscountError('code');
      });
  });

  drawer.addEventListener('click', function(e) {
    var remove = e.target.closest('[data-rw-discount-remove]');
    if (!remove) return;
    e.preventDefault();
    var code = remove.closest('[data-rw-discount-code]').dataset.rwDiscountCode;
    var remaining = appliedDiscountCodes(drawer).filter(function(c) { return c !== code; });
    updateDiscounts(remaining)
      .then(function(json) { renderDrawer(json.sections && json.sections[SECTION_ID]); })
      .catch(function(err) { console.error('[cart-drawer] discount removal failed', err); });
  });

  /* ---------- quantity / remove ---------- */

  function changeLine(line, quantity) {
    setLoading(true);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line: line,
        quantity: quantity,
        sections: [SECTION_ID],
        sections_url: window.location.pathname
      }),
      credentials: 'same-origin'
    })
      .then(function(res) { return res.json(); })
      .then(function(json) {
        if (json.sections) {
          renderDrawer(json.sections[SECTION_ID]);
        } else {
          // e.g. 422 when requesting more than is in stock
          return refresh();
        }
      })
      .catch(function(err) {
        console.error('[cart-drawer] change failed', err);
        return refresh();
      })
      .finally(function() { setLoading(false); });
  }

  function lineOf(el) {
    var item = el.closest('[data-line]');
    return item ? parseInt(item.dataset.line, 10) : null;
  }

  drawer.addEventListener('click', function(e) {
    var recStep = e.target.closest('[data-rw-rec-step]');
    if (recStep) {
      e.preventDefault();
      var recInput = recStep.closest('form').querySelector('[name="quantity"]');
      recInput.value = Math.max(1, (parseInt(recInput.value, 10) || 1) + parseInt(recStep.dataset.rwRecStep, 10));
      return;
    }
    var step = e.target.closest('[data-rw-qty-step]');
    if (step) {
      e.preventDefault();
      var input = step.closest('[data-line]').querySelector('[data-rw-qty-input]');
      var next = Math.max(1, (parseInt(input.value, 10) || 0) + parseInt(step.dataset.rwQtyStep, 10));
      if (next === parseInt(input.value, 10)) return;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    var remove = e.target.closest('[data-rw-remove]');
    if (remove) {
      e.preventDefault();
      changeLine(lineOf(remove), 0);
    }
  });

  drawer.addEventListener('change', function(e) {
    if (!e.target.matches('[data-rw-qty-input]')) return;
    var line = lineOf(e.target);
    var qty = Math.max(0, parseInt(e.target.value, 10) || 0);
    clearTimeout(qtyTimers[line]);
    qtyTimers[line] = setTimeout(function() { changeLine(line, qty); }, 300);
  });

  /* ---------- recommendations (Search & Discovery) ---------- */

  function fetchRecs(productId, intent) {
    var key = productId + ':' + intent;
    if (!recsCache[key]) {
      var url = recsUrl + '?section_id=' + RECS_SECTION_ID +
        '&product_id=' + encodeURIComponent(productId) +
        '&limit=' + (recsLimit + 4) +
        '&intent=' + intent;
      recsCache[key] = fetch(url, { credentials: 'same-origin' })
        .then(function(res) { return res.ok ? res.text() : ''; })
        .catch(function() { return ''; });
    }
    return recsCache[key].then(function(html) {
      return html ? Array.prototype.slice.call(parse(html).querySelectorAll('[data-rw-rec-card]')) : [];
    });
  }

  function loadRecommendations() {
    var wrapper = drawer.querySelector('[data-rw-recs]');
    var list = drawer.querySelector('[data-rw-recs-list]');
    if (!recsEnabled || !wrapper || !list) return;

    var cartIds = [];
    drawer.querySelectorAll('[data-line][data-product-id]').forEach(function(el) {
      if (cartIds.indexOf(el.dataset.productId) === -1) cartIds.push(el.dataset.productId);
    });

    var key = cartIds.join(',');
    if (key === recsKey) return;
    recsKey = key;

    var picked = [];
    var seen = {};
    function take(cards) {
      cards.forEach(function(card) {
        var id = card.dataset.productId;
        if (picked.length >= recsLimit || seen[id] || cartIds.indexOf(id) > -1) return;
        seen[id] = true;
        picked.push(card);
      });
    }

    // Most recently added items are first in cart.items, so they drive the picks.
    var sources = cartIds.slice(0, MAX_RECS_SOURCES);
    var chain = Promise.resolve();
    recsIntents.forEach(function(intent) {
      chain = chain.then(function() {
        if (picked.length >= recsLimit || !sources.length) return;
        return Promise.all(sources.map(function(id) { return fetchRecs(id, intent); }))
          .then(function(results) { results.forEach(take); });
      });
    });

    chain.then(function() {
      if (key !== recsKey) return; // cart changed while loading
      if (picked.length < recsLimit) {
        var fallback = drawer.querySelector('template[data-rw-recs-fallback]');
        if (fallback) take(Array.prototype.slice.call(fallback.content.querySelectorAll('[data-rw-rec-card]')));
      }
      list.innerHTML = '';
      picked.forEach(function(card) { list.appendChild(document.importNode(card, true)); });
      wrapper.hidden = picked.length === 0;
      if (window.yotpoWidgetsContainer && typeof window.yotpoWidgetsContainer.initWidgets === 'function') {
        window.yotpoWidgetsContainer.initWidgets();
      }
    });
  }

  loadRecommendations();

  window.RWCartDrawer = { open: open, close: close, refresh: refresh };
})();
